// Utility to compare arrays by id
export function arrayDiffById(a: any[], b: any[]) {
    const bIds = new Set(b.map(v => v.id));
    return a.filter(v => !bIds.has(v.id));
}

// Helper to get videos from other projects attached to this project
export function getAttachedVideosFromOtherProjects(videos: any[], currentProjectId: string) {
    return videos.filter(
        (video) =>
            Array.isArray(video.data.projects) &&
            video.data.projects.includes(currentProjectId) &&
            video.data.projects[0] !== currentProjectId && // not belonging to this project
            !video.data.delete &&
            video.data.status === 'COMPLETE'
    );
}

// Data helpers and decoding utilities for SDK data

// Decodes base64-encoded landmark data
export function decodeLandmarks(lm: any) {
  const numLandmarks = 68;
  const frames: { x: number[]; y: number[] }[] = [];
  for (let i = 0; i < numLandmarks; i++) {
    let xArr: number[] = [], yArr: number[] = [];
    try {
      xArr = atob(lm[`x${i}`]).split(',').map(Number);
    } catch (e) {
      xArr = Array(lm[`x0`] ? atob(lm[`x0`]).split(',').length : 0).fill(-1);
    }
    try {
      yArr = atob(lm[`y${i}`]).split(',').map(Number);
    } catch (e) {
      yArr = Array(lm[`y0`] ? atob(lm[`y0`]).split(',').length : 0).fill(-1);
    }
    for (let f = 0; f < xArr.length; f++) {
      if (!frames[f]) frames[f] = { x: [], y: [] };
      frames[f].x[i] = xArr[f];
      frames[f].y[i] = yArr[f];
    }
  }
  return frames;
}

// Decodes video health from base64
export function decodeVideoHealth(encodedQualityString: string) {
  const binaryData = atob(encodedQualityString);
  const buffer = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    buffer[i] = binaryData.charCodeAt(i);
  }
  const dataView = new DataView(buffer.buffer);
  let offset = 0;
  const numArrays = dataView.getUint8(offset);
  offset += 1;
  const lengths: number[] = [];
  for (let i = 0; i < numArrays; i++) {
    lengths.push(dataView.getUint32(offset, true));
    offset += 4;
  }
  const keys = [
    'face_nondetection',
    'affect_invalid',
    'image_unacceptable',
    'image_blurry',
    'image_dark',
    'image_bright',
    'head_angles_invalid',
  ];
  const result: Record<string, number[]> = {};
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const length = lengths[i];
    result[key] = [];
    for (let j = 0; j < length; j++) {
      result[key].push(dataView.getUint32(offset, true));
      offset += 4;
    }
  }
  return result;
}

// Decodes valence/arousal array from base64
export function decodeVA(base64String: string | null): { valence: number | null; arousal: number | null }[] | null {
  if (!base64String) return [];
  try {
    const binaryString = atob(base64String);
    const buffer = new ArrayBuffer(binaryString.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < binaryString.length; i++) {
      view[i] = binaryString.charCodeAt(i);
    }
    const dataView = new DataView(buffer);
    const numPairs = buffer.byteLength / 8;
    const vaPairs: { valence: number | null; arousal: number | null }[] = [];
    for (let i = 0; i < numPairs; i++) {
      const offset = i * 8;
      const valence = dataView.getFloat32(offset, true);
      const arousal = dataView.getFloat32(offset + 4, true);
      vaPairs.push({
        valence: isFinite(valence) ? valence : null,
        arousal: isFinite(arousal) ? arousal : null,
      });
    }
    return vaPairs;
  } catch (error) {
    return [];
  }
}

// Decodes head angles from base64
export function decodeHeadAngles(base64String: string | null): { pitch: number | null; roll: number | null; yaw: number | null }[] | null {
  if (!base64String) return null;
  try {
    const binaryString = atob(base64String);
    const buffer = new ArrayBuffer(binaryString.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < binaryString.length; i++) {
      view[i] = binaryString.charCodeAt(i);
    }
    const dataView = new DataView(buffer);
    const numTriples = buffer.byteLength / 12;
    const headData: { pitch: number | null; roll: number | null; yaw: number | null }[] = [];
    for (let i = 0; i < numTriples; i++) {
      const offset = i * 12;
      const pitch = dataView.getFloat32(offset, true);
      const roll = dataView.getFloat32(offset + 4, true);
      const yaw = dataView.getFloat32(offset + 8, true);
      headData.push({
        pitch: isFinite(pitch) ? pitch : null,
        roll: isFinite(roll) ? roll : null,
        yaw: isFinite(yaw) ? yaw : null,
      });
    }
    return headData;
  } catch (error) {
    return null;
  }
}

// Converts SDK schema v2 data to frontend format
export function convertSchema2(data: any) {
  if (!data || typeof data !== 'object') {
    return {};
  }
  let face_bounding_boxes: any[] = [];
  if (data.bb) {
    try {
      const decodeCSV = (b64: string) => atob(b64).split(',').map(Number);
      const xArr = decodeCSV(data.bb.x);
      const yArr = decodeCSV(data.bb.y);
      const wArr = decodeCSV(data.bb.width);
      const hArr = decodeCSV(data.bb.height);
      const validArr = decodeCSV(data.bb.valid);
      face_bounding_boxes = xArr.map((x, i) =>
        validArr[i] === 1
          ? { x: Math.round(x), y: Math.round(yArr[i]), width: Math.round(wArr[i]), height: Math.round(hArr[i]) }
          : { x: -1, y: -1, width: -1, height: -1 }
      );
    } catch (err) {
      console.error('[convertSchema2] Failed to decode bb:', err, data.bb);
      face_bounding_boxes = [];
    }
  } else {
    face_bounding_boxes = [];
  }
  let landmarks: any[] = [];
  if (data.lm) {
    try {
      landmarks = decodeLandmarks(data.lm);
    } catch (err) {
      console.error('[convertSchema2] Failed to decode lm:', err, data.lm);
      landmarks = [];
    }
  }
  let va: any[] = [];
  if (typeof data.va === 'string') {
    try {
      va = decodeVA(data.va) ?? [];
    } catch (err) {
      console.error('[convertSchema2] Failed to decode va:', err, data.va);
      va = [];
    }
  } else if (Array.isArray(data.va)) {
    va = data.va;
  } else {
    va = [];
  }
  const converted: any = {};
  if ('es' in data) converted.emotional_saliency = data.es;
  if ('ag' in data) converted.emotional_agility = data.ag;
  if ('gs' in data) converted.graph_stats = data.gs;
  if ('interesting_events' in data) converted.interesting_events = data.interesting_events;
  converted.va = va;
  if ('st' in data) converted.va_stats = data.st;
  if ('vq' in data) converted.va_quality = data.vq;
  if ('sp' in data) converted.va_speed = data.sp;
  if ('measurability' in data) converted.measurability = data.measurability;
  if ('qu' in data) {
    converted.video_health = data.qu ? decodeVideoHealth(data.qu) : {};
  } else {
    converted.video_health = {};
  }
  if ('em' in data) converted.emotions = data.em;
  if ('he' in data) converted.head_angles = data.he ? decodeHeadAngles(data.he) : [];
  if ('sv' in data) converted.schema_version = data.sv;
  converted.face_bounding_boxes = face_bounding_boxes;
  converted.landmarks = landmarks;
  return converted;
}

// Utility for safe SDK data access
export type SDKDataField = keyof ReturnType<typeof convertSchema2>;
export function getSDKField<T = any>(sdkData: any, field: SDKDataField, defaultValue?: T): T | undefined {
  if (!sdkData) return defaultValue;
  return sdkData[field] !== undefined ? sdkData[field] : defaultValue;
}

/**
 * Centralized SDK field lists for each view/context.
 * Use these to ensure consistent, minimal, and correct field requests across the app.
 */
export const SDK_FIELDS = {
  analytics: [
    'es', // emotional_saliency
    'ag', // emotional_agility
    'gs', // graph_stats
    'interesting_events',
    'va',
    'st', // va_stats
    'vq', // va_quality
    'sp', // va_speed
    'measurability',
    'qu', // video_health
    'em', // emotions
    'he', // head_angles
    'sv', // schema_version
    'bb', // face_bounding_boxes
    'lm', // landmarks
  ],
  projectList: [
    'va',
    'st', // va_stats
    'sp', // va_speed
    'gs', // graph_stats
    'interesting_events',
    'measurability',
    'es', // emotional_saliency
    'ag', // emotional_agility
    'vq', // va_quality
    'qu', // video_health
    'em', // emotions
    'sv', // schema_version (added to ensure correct decoding)
    // 'bb', // overlays not needed here
    // 'lm', // landmarks not needed here
  ],
  projectsOverview: [
    'measurability',
    'qu', // video_health
    'em', // emotions
    'interesting_events',
    'sv', // schema_version (added to ensure correct decoding)
    'va',
    // 'bb', // overlays not needed here
    // 'lm', // landmarks not needed here
  ],
  // Add more contexts as needed
};